// src/components/PortalAluno/VisualizadorCertificado.tsx
// UPDATED: 2026-03-27
// ✅ Só mostra certificados quando diploma foi solicitado ou aprovado
// ✅ Se não há solicitação, mostra estado vazio orientando o aluno

import React, { useMemo, useState } from 'react';
import {
  Award, Download, Share2, Search,
  Calendar, Clock, User, CheckCircle, AlertCircle,
  ArrowLeft, FileCheck, GraduationCap, Loader2,
  ArrowRight, RefreshCw, AlertTriangle,
} from 'lucide-react';
import { useStudentProfile } from '../../hooks/useStudentProfile';

interface Certificado {
  id: string;
  nomeCurso: string;
  dataEmissao: string;
  cargaHoraria: number;
  instrutorNome: string;
  certificadoUrl: string;
  qrCode: string;
  status: 'disponivel' | 'pendente' | 'rejeitado';
  requestId?: string;
}

const StatusBadge: React.FC<{ status: Certificado['status'] }> = ({ status }) => {
  if (status === 'disponivel') return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200">
      <CheckCircle className="w-3 h-3"/> Aprovado
    </span>
  );
  if (status === 'rejeitado') return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-xl bg-red-50 text-red-700 border border-red-200">
      <AlertCircle className="w-3 h-3"/> Recusado
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-xl bg-amber-50 text-amber-700 border border-amber-200">
      <AlertCircle className="w-3 h-3"/> Em Análise
    </span>
  );
};

const VisualizadorCertificado: React.FC = () => {
  const { student, loading, error, refetch } = useStudentProfile();
  const [selected, setSelected] = useState<Certificado | null>(null);
  const [query, setQuery]       = useState('');

  // Monta lista de certificados a partir das diplomaRequests do aluno
  const certificados: Certificado[] = useMemo(() => {
    if (!student?.diplomaRequests?.length) return [];
    return student.diplomaRequests.map(req => ({
      id:             req.id,
      nomeCurso:      student.courseName ?? 'Curso Profissional',
      dataEmissao:    req.resolvedAt ?? req.requestedAt,
      cargaHoraria:   student.courseWorkload ?? 0,
      instrutorNome:  student.affiliate?.schoolName ?? 'Federação dos Profissionais da Beleza',
      certificadoUrl: '', // URL real viria do backend quando disponível
      qrCode:         `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`${window.location.origin}/verificar/${student.systemId}/${req.id}`)}`,
      status:         req.status === 'approved' ? 'disponivel' : req.status === 'rejected' ? 'rejeitado' : 'pendente',
      requestId:      req.id,
    }));
  }, [student]);

  const filtrados = useMemo(() => {
    const q = query.trim().toLowerCase();
    return certificados.filter(c =>
      !q || c.nomeCurso.toLowerCase().includes(q) || c.id.toLowerCase().includes(q)
    );
  }, [certificados, query]);

  // Loading
  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-8 h-8 text-amber-500 animate-spin"/>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-400"/>
      <p className="text-sm text-gray-600">{error}</p>
      <button onClick={refetch} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50">
        <RefreshCw className="w-4 h-4"/> Tentar novamente
      </button>
    </div>
  );

  // ── SEM NENHUMA SOLICITAÇÃO ───────────────────────────────────────────────
  if (certificados.length === 0) return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3.5">
        <div className="relative flex-shrink-0">
          <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full"/>
          <img src="/icons/icon-certificados.png" alt="Certificados"
            className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"
            onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
        </div>
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Meus Certificados</h1>
          <p className="text-xs text-gray-400 mt-1">Diplomas e certificados emitidos</p>
        </div>
      </div>

      {/* Estado vazio */}
      <div className="bg-white rounded-3xl border border-gray-100
        shadow-[0_1px_8px_rgba(0,0,0,0.05)] p-10 flex flex-col items-center text-center">
        <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-5"
          style={{ background: 'linear-gradient(135deg,rgba(212,175,55,0.15),rgba(212,175,55,0.05))' }}>
          <GraduationCap className="w-10 h-10 text-amber-500"/>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Nenhum diploma solicitado</h3>
        <p className="text-sm text-gray-500 max-w-sm mb-6 leading-relaxed">
          Você ainda não solicitou nenhum diploma ou certificado.
          Quando fizer uma solicitação, ela aparecerá aqui para acompanhar o status.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 max-w-sm text-left">
          <p className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5"/> Como solicitar
          </p>
          <p className="text-xs text-amber-700 leading-relaxed">
            Acesse a aba <strong>"Início"</strong> e clique em <strong>"Solicitar Diploma"</strong>,
            ou peça ao seu filiado que realize a solicitação.
          </p>
        </div>
      </div>
    </div>
  );

  // ── DETALHE ───────────────────────────────────────────────────────────────
  if (selected) return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3.5">
        <button onClick={() => setSelected(null)}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5 text-gray-600"/>
        </button>
        <div>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
            {selected.nomeCurso}
          </h1>
          <p className="text-xs text-gray-400 mt-1">{selected.id}</p>
        </div>
      </div>

      <StatusBadge status={selected.status}/>

      {/* Preview ou placeholder */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        {selected.status === 'disponivel' && selected.certificadoUrl ? (
          <img src={selected.certificadoUrl} alt="Certificado" className="w-full object-cover"/>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <FileCheck className="w-14 h-14 text-gray-200"/>
            <p className="text-sm text-gray-400 font-medium">
              {selected.status === 'pendente'
                ? 'Documento em análise — disponível após aprovação'
                : selected.status === 'rejeitado'
                ? 'Solicitação recusada pela Federação'
                : 'Certificado sendo gerado…'}
            </p>
          </div>
        )}
      </div>

      {/* Detalhes */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] p-6">
        <div className="grid grid-cols-2 gap-4">
          {[
            { icon: Award,    label: 'Curso',          value: selected.nomeCurso },
            { icon: User,     label: 'Instituição',    value: selected.instrutorNome },
            { icon: Clock,    label: 'Carga Horária',  value: selected.cargaHoraria > 0 ? `${selected.cargaHoraria}h` : '—' },
            { icon: Calendar, label: 'Data',           value: new Date(selected.dataEmissao).toLocaleDateString('pt-BR') },
          ].map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-amber-600"/>
              </div>
              <div>
                <p className="text-xs text-gray-400">{label}</p>
                <p className="text-sm font-semibold text-gray-800">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* QR + ações */}
      {selected.status === 'disponivel' && (
        <div className="flex flex-wrap gap-3">
          {selected.certificadoUrl && (
            <button onClick={() => window.open(selected.certificadoUrl, '_blank')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium
                text-[#1a1a1c] hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
              <Download className="w-4 h-4"/> Baixar PDF
            </button>
          )}
          <button onClick={() => { if (navigator.share) navigator.share({ title: selected.nomeCurso, url: selected.certificadoUrl }); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium
              text-gray-700 border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all">
            <Share2 className="w-4 h-4"/> Compartilhar
          </button>
        </div>
      )}
    </div>
  );

  // ── LISTA ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full"/>
            <img src="/icons/icon-certificados.png" alt="Certificados"
              className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"
              onError={e => { (e.currentTarget as HTMLImageElement).style.display='none'; }}/>
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
              Meus Certificados
            </h1>
            <p className="text-xs text-gray-400 mt-1">
              {certificados.length} solicitação{certificados.length !== 1 ? 'ões' : ''}
            </p>
          </div>
        </div>
        <button onClick={refetch}
          className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400 hover:text-gray-600">
          <RefreshCw className="w-4 h-4"/>
        </button>
      </div>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Buscar por curso ou ID…"
          className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm
            focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"/>
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {filtrados.map(cert => (
          <button key={cert.id}
            onClick={() => setSelected(cert)}
            className="w-full bg-white rounded-2xl border border-gray-100
              shadow-[0_1px_6px_rgba(0,0,0,0.04)] p-5 text-left
              hover:border-amber-200 hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]
              transition-all group">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: cert.status === 'disponivel' ? 'rgba(212,175,55,0.15)' : 'rgba(0,0,0,0.05)' }}>
                <Award className="w-6 h-6" style={{ color: cert.status === 'disponivel' ? '#d4af37' : '#9ca3af' }}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <p className="font-semibold text-gray-900 text-sm truncate">{cert.nomeCurso}</p>
                  <StatusBadge status={cert.status}/>
                </div>
                <p className="text-xs text-gray-400">
                  {new Date(cert.dataEmissao).toLocaleDateString('pt-BR')}
                  {cert.cargaHoraria > 0 && ` · ${cert.cargaHoraria}h`}
                </p>
                <p className="text-[11px] text-gray-400 mt-0.5 font-mono">{cert.id}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-gray-400 flex-shrink-0 mt-1"/>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default VisualizadorCertificado;
